import { Alert, App, Form, Input, Modal, Select } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useClientsGetClient } from '@/api/generated/endpoints/clients/clients';
import {
  useCorrectionsChangeClient,
  useCorrectionsRestoreCancelled,
  useCorrectionsUndoAcceptance,
} from '@/api/generated/endpoints/corrections/corrections';
import { useShipmentsGetShipment } from '@/api/generated/endpoints/shipments/shipments';
import type { AuditEntryOut, ShipmentStatus } from '@/api/generated/model';
import { ClientSelect } from '@/components/ClientSelect';
import { confirmAction } from '@/components/confirmAction';
import { problemMessage } from '@/i18n/problem';
import { useCorrectionRefresh } from '@/pages/audit/useCorrectionRefresh';

const REASON_RULES = [{ required: true, whitespace: true, min: 3 }];

function historyId(entry: AuditEntryOut): number {
  return Number(entry.key.split(':')[1]);
}

function ReasonField() {
  const { t } = useTranslation();
  return (
    <Form.Item
      name="reason"
      label={t('corrections.reason')}
      extra={t('corrections.reasonHint')}
      rules={REASON_RULES.map((r) => ({ ...r, message: t('corrections.reasonRequired') }))}
    >
      <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} maxLength={2000} />
    </Form.Item>
  );
}

/** Отменить приёмку клиентом или вернуть отменённую в черновик: только причина. */
export function ShipmentStatusModal({
  entry,
  kind,
  onClose,
}: {
  entry: AuditEntryOut;
  kind: 'undo_acceptance' | 'restore_cancelled';
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { message, modal } = App.useApp();
  const [form] = Form.useForm<{ reason: string }>();
  const [error, setError] = useState<string | null>(null);
  const refresh = useCorrectionRefresh();
  const undo = useCorrectionsUndoAcceptance();
  const restore = useCorrectionsRestoreCancelled();
  const number = entry.shipment?.name ?? '';
  const to = entry.status_to ?? undefined;
  const statusLabel = (s: ShipmentStatus | undefined) =>
    s ? t(`status.shipment.${s}.label`) : '—';

  async function submit({ reason }: { reason: string }) {
    setError(null);
    const undoing = kind === 'undo_acceptance';
    const ok = await confirmAction(modal, {
      title: t(`corrections.${kind}.confirmTitle`, { number }),
      details: [
        t('corrections.confirmWho', { who: entry.actor?.name ?? '—' }),
        t('corrections.confirmField', {
          field: t('corrections.field.status'),
          before: statusLabel(to),
          after: statusLabel(undoing ? 'shipped' : 'draft'),
        }),
        t(`corrections.${kind}.consequence`),
        t('corrections.confirmReason', { reason: reason.trim() }),
      ],
      okText: t(`corrections.${kind}.ok`),
      cancelText: t('common.cancel'),
    });
    if (!ok) return;
    try {
      const vars = { historyId: historyId(entry), data: { reason: reason.trim() } };
      await (undoing ? undo.mutateAsync(vars) : restore.mutateAsync(vars));
      void message.success(t('corrections.done'));
      await refresh();
      onClose();
    } catch (e) {
      setError(problemMessage(e));
    }
  }

  return (
    <Modal
      open
      title={t(`corrections.${kind}.title`, { number })}
      okText={t('corrections.next')}
      onOk={() => {
        form.submit();
      }}
      onCancel={onClose}
      okButtonProps={{ loading: undo.isPending || restore.isPending, danger: true }}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" onFinish={(v) => void submit(v)}>
        <ReasonField />
      </Form>
      {error && <Alert type="error" showIcon message={error} />}
    </Modal>
  );
}

interface ClientValues {
  client_id: number | null;
  delivery_point_id: number | null;
  reason: string;
}

/** Сменить клиента или точку доставки у отгрузки. */
export function ChangeClientModal({
  entry,
  onClose,
}: {
  entry: AuditEntryOut;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { message, modal } = App.useApp();
  const [form] = Form.useForm<ClientValues>();
  const [error, setError] = useState<string | null>(null);
  const refresh = useCorrectionRefresh();
  const change = useCorrectionsChangeClient();
  const shipment = useShipmentsGetShipment(entry.shipment?.id ?? 0);
  const s = shipment.data;
  const clientId = Form.useWatch('client_id', form);
  const client = useClientsGetClient(clientId ?? 0, { query: { enabled: !!clientId } });
  const points = (client.data?.delivery_points ?? []).filter((p) => p.is_active);

  const clientName = (c: { name: string; brand?: string | null } | null | undefined) =>
    c ? (c.brand ?? c.name) : '—';

  async function submit(v: ClientValues) {
    if (!s || v.client_id === null) return;
    setError(null);
    const pointId = v.delivery_point_id ?? null;
    if (v.client_id === s.client.id && pointId === (s.delivery_point?.id ?? null)) {
      setError(t('corrections.nothingChanged'));
      return;
    }
    const point = points.find((p) => p.id === pointId);
    const ok = await confirmAction(modal, {
      title: t('corrections.change_client.confirmTitle', { number: s.number }),
      details: [
        t('corrections.confirmWho', { who: entry.actor?.name ?? '—' }),
        t('corrections.confirmField', {
          field: t('corrections.field.client'),
          before: clientName(s.client),
          after: clientName(client.data),
        }),
        t('corrections.confirmField', {
          field: t('corrections.field.delivery_point'),
          before: s.delivery_point?.name ?? '—',
          after: point?.name ?? '—',
        }),
        t('corrections.confirmReason', { reason: v.reason.trim() }),
      ],
      okText: t('corrections.change_client.ok'),
      cancelText: t('common.cancel'),
    });
    if (!ok) return;
    try {
      await change.mutateAsync({
        historyId: historyId(entry),
        data: { client_id: v.client_id, delivery_point_id: pointId, reason: v.reason.trim() },
      });
      void message.success(t('corrections.done'));
      await refresh();
      onClose();
    } catch (e) {
      setError(problemMessage(e));
    }
  }

  return (
    <Modal
      open
      title={t('corrections.change_client.title', { number: s?.number ?? '' })}
      okText={t('corrections.next')}
      onOk={() => {
        form.submit();
      }}
      onCancel={onClose}
      okButtonProps={{ loading: change.isPending, disabled: !s }}
      destroyOnHidden
    >
      {s && (
        <Form<ClientValues>
          form={form}
          layout="vertical"
          initialValues={{
            client_id: s.client.id,
            delivery_point_id: s.delivery_point?.id ?? null,
            reason: '',
          }}
          onFinish={(v) => void submit(v)}
        >
          <Form.Item
            name="client_id"
            label={t('corrections.field.client')}
            rules={[{ required: true, message: t('shipment.clientRequired') }]}
          >
            <ClientSelect
              value={clientId ?? null}
              onChange={(id) => {
                form.setFieldsValue({ client_id: id, delivery_point_id: null });
              }}
            />
          </Form.Item>
          <Form.Item name="delivery_point_id" label={t('corrections.field.delivery_point')}>
            <Select<number>
              allowClear
              disabled={!clientId}
              options={points.map((p) => ({ value: p.id, label: p.name }))}
            />
          </Form.Item>
          <ReasonField />
        </Form>
      )}
      {error && <Alert type="error" showIcon message={error} />}
    </Modal>
  );
}
